import React, { createContext, useCallback, useState } from 'react';

import { useFetch, useForm, useMutation, useSegment } from 'utils/hooks';
import { createFormData, sanitizePayload } from './methods';
import { DEFAULT_FORM_DATA, FORM_SCHEMA } from './stubs';

/**
 * @type {React.Context<{
 *   entity: "exchanges" | "purchases",
 *   isError: boolean,
 *   isLoading: boolean,
 *   data: import('zod').z.infer<typeof FORM_SCHEMA>,
 *   initialData: import('zod').z.infer<typeof FORM_SCHEMA>,
 *   productTypeOptions: Array<{ label: string, value: string }>,
 *   productCategoryOptions: Array<{ label: string, value: string }>,
 *   collectionOptions: Array<{ label: string, value: string }>,
 *   isDirty: boolean,
 *   errors: Partial<import('zod').z.infer<typeof FORM_SCHEMA>>,
 *   onChange: (updates: Partial<import('zod').z.infer<typeof FORM_SCHEMA>>, runValidation?: boolean) => void,
 *   onReset: () => void,
 *   onValidate: (data?: Partial<import('zod').z.infer<typeof FORM_SCHEMA>>) => boolean,
 *   onReload: () => void,
 *   isProcessing: boolean,
 *   onSave: (payload?: Record<string, any>, callback?: import("utils/types").MutationCallback) => void
 * }>}
 */
export const ProductsCatalogContext = createContext();

export function ProductsCatalogProvider({ entity = null, children = null }) {
  const { track } = useSegment();

  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [productCategoryOptions, setProductCategoryOptions] = useState([]);
  const [collectionOptions, setCollectionOptions] = useState([]);

  const {
    data,
    errors,
    isDirty,
    initialData,
    onChange,
    onReset,
    onValidate,
    onSetInitialData
  } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: DEFAULT_FORM_DATA
  });

  const initialize = useCallback(
    data => {
      const {
        formData,
        productTypeOptions,
        productCategoryOptions,
        collectionOptions
      } = createFormData(data);
      onSetInitialData(formData);
      setProductTypeOptions(productTypeOptions);
      setProductCategoryOptions(productCategoryOptions);
      setCollectionOptions(collectionOptions);
    },
    [onSetInitialData]
  );

  const { isError, isLoading, fetchData } = useFetch(
    `configure.${entity}.catalog.get`,
    {
      onSuccess: data => initialize(data)
    }
  );

  const { mutate, isProcessing } = useMutation(
    `configure.${entity}.catalog.put`,
    'PUT',
    {
      payload: () => sanitizePayload(data),
      errorMessage: `Unable to update the ${entity} catalog. Please try again later.`,
      successMessage: `Successfully updated the ${entity} catalog.`,
      onValidate: () => {
        const isValid = onValidate(data);
        console.log('[CATALOG] isValid', isValid);
        return isValid;
      },
      onSuccess: data => {
        initialize(data);
        track(`Updated ${entity} Catalog`, { entity });
      }
    }
  );

  return (
    <ProductsCatalogContext.Provider
      value={{
        entity,
        data,
        isError,
        isLoading,
        productTypeOptions,
        productCategoryOptions,
        collectionOptions,
        errors,
        isDirty,
        initialData,
        onChange,
        onReset,
        onReload: fetchData,
        isProcessing,
        onSave: mutate
      }}
    >
      {children}
    </ProductsCatalogContext.Provider>
  );
}
